import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  initializeAuth,
  logout,
  selectIsAuthenticated,
  selectIsInitialized,
  setInitialized,
  setSession,
  setTenantId,
} from '../features/auth/authSlice';
import { authApiSlice, useLazyVerifySessionQuery } from '../features/auth/api/authApiSlice';
import { apiSlice } from '../store/apiSlice';
import type { RootState } from '../store/store';
import { Logger } from '../lib/utils';

interface AuthInitializerProps {
  children: React.ReactNode;
}

function readStoredTenantId(): number | null {
  try {
    const raw = localStorage.getItem('tenantId');
    if (!raw || !/^\d+$/.test(raw)) return null;
    const parsed = Number.parseInt(raw, 10);
    return parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

export const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialized = useSelector((state: RootState) => selectIsInitialized(state));
  const isAuthenticated = useSelector((state: RootState) => selectIsAuthenticated(state));
  const [verifySession] = useLazyVerifySessionQuery();

  useEffect(() => {
    const bootstrap = async () => {
      if (isInitialized) return;

      dispatch(initializeAuth());

      try {
        const session = await verifySession().unwrap();
        let resolvedTenant =
          session.tenantId ?? session.user.empresaId ?? readStoredTenantId();

        if (session.user.role === 'SUPERADMIN' && resolvedTenant == null) {
          const empresasResult = await dispatch(
            authApiSlice.endpoints.listEmpresas.initiate()
          ).unwrap();
          resolvedTenant = empresasResult.data[0]?.id ?? null;
        }

        dispatch(
          setSession({
            user: session.user,
            tenantId: resolvedTenant,
          })
        );
        if (resolvedTenant) {
          dispatch(setTenantId(resolvedTenant));
        }
      } catch {
        dispatch(logout());
      } finally {
        dispatch(setInitialized(true));
      }
    };

    void bootstrap();
  }, [dispatch, isInitialized, verifySession]);

  useEffect(() => {
    if (isInitialized && !isAuthenticated && location.pathname !== '/login') {
      Logger.debug('AuthInitializer: Redirigiendo a login');
      navigate('/login', {
        replace: true,
        state: { from: location },
      });
    }
  }, [isInitialized, isAuthenticated, location, navigate]);

  if (!isInitialized) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
      </div>
    );
  }

  return <>{children}</>;
};
